/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import dynamic from 'next/dynamic';
import { getLocalStorage } from '@tcp/core/src/utils/localStorageManagement';
import { isCanada } from '@tcp/core/src/utils';
import { scrollToBreadCrumb } from '@tcp/core/src/utils/utils.web';
import ToggleSwitch from '@tcp/core/src/components/common/atoms/ToggleSwitch';
import { changeOnlyFirstLetterToCapital } from '@tcp/core/src/utils/utils';
import { getIsAllowOnlineProductPickupEnabled } from '@tcp/core/src/reduxStore/selectors/session.selectors';
import withStyles from '../../../../../../common/hoc/withStyles';
import styles, {
  BopisFilterWrapper,
  StoreInfo,
  StoreChangeWrapper,
  ChangeStoreLink,
  BopisStoreName,
  SwitchFilter,
} from '../styles/BopisFilter.style';
import createPayload from '../../utils/filterAnalytics';
import { BopisFilterLabels } from './BopisFilter.labels';
import { getLabelFromCopyOrFromTranslation } from '../../../../../../../utils/labels/labelUtilty';
import {
  getCurrentFeatureBrandAttribute,
  getFeatureBrand,
} from '../../../../SearchDetail/container/SearchDetail.util';

const StoreLocatorModal = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "stor-locator-modal" */
      '@tcp/core/src/components/common/organisms/StoreLocatorModal'
    ),
  { ssr: false }
);

const BopisFilter = ({
  isBopisFilterOn,
  className,
  onStoreChangeLinkClick,
  onSubmit,
  formValues = {},
  getProducts,
  updateFormValue,
  setBopisFilterStateActn,
  defaultStoreData,
  resetSuggestedStores,
  trackClickAnalyticsBopisFilter,
  trackAnalyticsClick,
  isFeatureBrandView,
  isFeatureBrandEnabled,
}) => {
  const bopisfilterlabels = getLabelFromCopyOrFromTranslation('PLP.plpTiles', BopisFilterLabels);
  const [storeData, setStoreData] = useState({});
  const [modalStoreSearchData, setModalStoreSearchData] = useState(null);
  const [bopisStoreSelectorModalOpen, setBopisStoreSelectorModalOpen] = useState(false);
  if (defaultStoreData && !isEqual(storeData, defaultStoreData)) {
    setStoreData(defaultStoreData);
  }
  useEffect(() => {
    if (!defaultStoreData) {
      setStoreData(defaultStoreData);
    }
  }, [defaultStoreData]);
  const handleChangeStoreOnKeyPress = (e) => {
    if (e.keyCode !== 32) return;
    e.preventDefault();
    onStoreChangeLinkClick();
  };

  const handleChangeStoreLinkClickFunc = () => {
    trackClickAnalyticsBopisFilter({ bopis_filter_metrics168: 1, customEvents: ['event168'] });
    if (onStoreChangeLinkClick) {
      onStoreChangeLinkClick();
    }
    resetSuggestedStores([]);
    setBopisStoreSelectorModalOpen(true);
    setModalStoreSearchData(storeData);
  };

  const handleChangeStoreLinkClick = () => {
    setTimeout(() => {
      handleChangeStoreLinkClickFunc();
    }, 0);
  };

  const handleBopisStoreModalCloseFunc = () => {
    setBopisStoreSelectorModalOpen(false);
    setModalStoreSearchData(null);
  };

  const handleBopisStoreModalClose = () => {
    setTimeout(() => {
      handleBopisStoreModalCloseFunc();
    }, 0);
  };

  const isStoreAvailable = storeData?.basicInfo?.id;
  let storeChangeText = bopisfilterlabels?.lbl_plp_select_store;
  let storeSelectionMessage = bopisfilterlabels?.lbl_plp_select_nearby_store;
  if (isStoreAvailable) {
    storeChangeText = bopisfilterlabels?.lbl_plp_change_store;
    storeSelectionMessage = bopisfilterlabels?.lbl_plp_select_at;
  }

  const toggleBopisFilter = () => {
    let tcpWebOnlyBopisFilter = isCanada()
      ? '-TCPWebOnlyFlagCanadaStore:1'
      : '-TCPWebOnlyFlagUSStore:1';

    if (getIsAllowOnlineProductPickupEnabled()) {
      tcpWebOnlyBopisFilter = '';
    }

    const storeFilterId = [
      `v_Quantity_${storeData?.basicInfo?.id?.slice(-4)}:[1 TO *]${tcpWebOnlyBopisFilter}`,
    ];
    let updatedFormValues = {};
    if (isBopisFilterOn) {
      updateFormValue('bopisStoreId', []);
      updatedFormValues = { ...formValues, bopisStoreId: [] };
    } else {
      updateFormValue('bopisStoreId', storeFilterId);
      updatedFormValues = { ...formValues, bopisStoreId: storeFilterId };

      if (formValues?.sort?.length > 0) {
        trackClickAnalyticsBopisFilter({
          bopis_cd50: createPayload(updatedFormValues),
          customEvents: ['eventb50'],
        });
      } else {
        trackClickAnalyticsBopisFilter({
          bopis_cd51: createPayload(updatedFormValues),
          customEvents: ['eventb51'],
        });
      }
    }
    setBopisFilterStateActn(!isBopisFilterOn);
    if (isFeatureBrandEnabled && isFeatureBrandView) {
      updatedFormValues = {
        ...updatedFormValues,
        [getCurrentFeatureBrandAttribute({})]: [getFeatureBrand()],
      };
    }
    onSubmit(updatedFormValues, false, getProducts);
    scrollToBreadCrumb();
  };

  const onStoreChange = (store) => {
    const storeFilterId = [`v_Quantity_${store?.basicInfo?.id?.slice(-4)}:[1 TO *]`];
    let updatedFormValues = {};
    if (isBopisFilterOn) {
      updateFormValue('bopisStoreId', storeFilterId);
      updatedFormValues = { ...formValues, bopisStoreId: storeFilterId };
      if (isFeatureBrandEnabled && isFeatureBrandView) {
        updatedFormValues = {
          ...updatedFormValues,
          [getCurrentFeatureBrandAttribute({})]: [getFeatureBrand()],
        };
      }
      onSubmit(updatedFormValues, false, getProducts);
    }
  };

  const isStorePresentForTogglingFunc = () => {
    setTimeout(() => {
      const store = JSON.parse(getLocalStorage('defaultStore'));
      if (store) {
        toggleBopisFilter();
      }
    }, 0);
  };

  const isStorePresentForToggling = () => {
    setTimeout(() => {
      isStorePresentForTogglingFunc();
    }, 0);
  };

  const renderStoreChangeLink = () => {
    return (
      <StoreChangeWrapper className="store-change-wrapper">
        <ChangeStoreLink
          className={`change-store-link ${
            storeChangeText === bopisfilterlabels?.lbl_plp_select_store && 'select-store'
          }`}
          role="button"
          tabIndex="0"
          onKeyDown={handleChangeStoreOnKeyPress}
          onClick={() => handleChangeStoreLinkClick()}
          underline
          noLink={false}
        >
          {storeChangeText}
        </ChangeStoreLink>
      </StoreChangeWrapper>
    );
  };

  const renderSwitchFilter = () => {
    return (
      <SwitchFilter className="switch-filter">
        <ToggleSwitch
          name="bopis-filter"
          id="bopis-switch"
          checked={isBopisFilterOn}
          onChange={isStorePresentForToggling}
          disabled={!isStoreAvailable && !isEqual(isStoreAvailable, undefined)}
        />
      </SwitchFilter>
    );
  };

  const renderStoreSelection = () => {
    const storeCity = storeData?.basicInfo?.address?.city;
    const storeState = storeData?.basicInfo?.address?.state;
    const mallName = changeOnlyFirstLetterToCapital(storeData?.basicInfo?.storeName);

    return (
      <span>
        <span>{`${storeSelectionMessage} `}</span>
        <BopisStoreName
          fontWeight="extrabold"
          fontFamily="secondary"
          component="span"
          className="bopis-storename mallName"
          textAlign="center"
        >
          {mallName ? `${mallName}` : ''}
        </BopisStoreName>
        <BopisStoreName
          fontFamily="secondary"
          component="span"
          className="bopis-storename"
          textAlign="center"
        >
          {storeCity ? `, ${storeCity}` : ''}
        </BopisStoreName>
        <BopisStoreName
          fontFamily="secondary"
          component="span"
          className="bopis-storename"
          textAlign="center"
        >
          {storeState ? `, ${storeState}` : ''}
        </BopisStoreName>
      </span>
    );
  };

  return (
    <BopisFilterWrapper className={`${className} bopis-filter-wrapper`}>
      {renderSwitchFilter()}
      <StoreInfo className="store-info">
        {renderStoreSelection()}
        {renderStoreChangeLink()}
      </StoreInfo>
      {bopisStoreSelectorModalOpen && (
        <StoreLocatorModal
          isOpen={bopisStoreSelectorModalOpen}
          onClose={handleBopisStoreModalClose}
          onStoreChange={onStoreChange}
          modalStoreSearchData={modalStoreSearchData}
          trackClickAnalyticsBopisFilter={trackClickAnalyticsBopisFilter}
          trackAnalyticsClick={trackAnalyticsClick}
        />
      )}
    </BopisFilterWrapper>
  );
};
BopisFilter.defaultProps = {
  onStoreChangeLinkClick: () => {},
  onSubmit: () => {},
  formValues: {},
  getProducts: () => {},
  updateFormValue: () => {},
  setBopisFilterStateActn: () => {},
  defaultStoreData: {},
  trackClickAnalyticsBopisFilter: () => {},
  trackAnalyticsClick: () => {},
};
BopisFilter.propTypes = {
  isBopisFilterOn: PropTypes.bool.isRequired,
  className: PropTypes.string.isRequired,
  onStoreChangeLinkClick: PropTypes.func,

  onSubmit: PropTypes.func,
  formValues: PropTypes.shape({}),
  getProducts: PropTypes.func,
  updateFormValue: PropTypes.func,
  setBopisFilterStateActn: PropTypes.func,
  resetSuggestedStores: PropTypes.func.isRequired,
  defaultStoreData: PropTypes.shape({}),
  trackClickAnalyticsBopisFilter: PropTypes.func,
  trackAnalyticsClick: PropTypes.func,
};
export default withStyles(BopisFilter, styles);
